import { DateTime } from "luxon";

import { formatOverdueDuration } from "@/utils/date";

export const FOLLOW_UP_URGENCY = {
  NONE: "none",
  EXPIRED: "expired",
  APPROACHING: "approaching",
  SCHEDULED: "scheduled",
} as const;

export type FollowUpUrgency =
  (typeof FOLLOW_UP_URGENCY)[keyof typeof FOLLOW_UP_URGENCY];

export const isFollowUpOverdue = (
  followUpAt: string | null | undefined,
): boolean => !!followUpAt && new Date(followUpAt).getTime() < Date.now();

// "Due now" for nudge purposes: scheduled for today (through end of day) or
// already overdue - i.e. the set the sidebar Nudges badge counts.
export const isFollowUpDueByEndOfToday = (
  followUpAt: string | null | undefined,
): boolean =>
  !!followUpAt && DateTime.fromISO(followUpAt) <= DateTime.now().endOf("day");

export const getFollowUpUrgency = (
  followUpAt: string | null | undefined,
): FollowUpUrgency => {
  if (!followUpAt) return FOLLOW_UP_URGENCY.NONE;

  const diffMs = new Date(followUpAt).getTime() - Date.now();

  if (diffMs < 0) return FOLLOW_UP_URGENCY.EXPIRED;
  if (diffMs <= 30 * 60 * 1000) return FOLLOW_UP_URGENCY.APPROACHING;
  return FOLLOW_UP_URGENCY.SCHEDULED;
};

export const formatFollowUpDue = (
  followUpAt: string | null | undefined,
): string | null => {
  if (!followUpAt) return null;

  const target = DateTime.fromISO(followUpAt);
  const now = DateTime.now();

  if (!target.isValid) return null;

  const diff = target.diff(now, ["days", "hours", "minutes"]).toObject();
  const days = Math.floor(diff.days ?? 0);
  const hours = Math.floor(diff.hours ?? 0);
  const minutes = Math.floor(diff.minutes ?? 0);
  const isPast = target < now;

  if (isPast) {
    const overdueMinutes = Math.floor(now.diff(target, "minutes").minutes);
    return formatOverdueDuration(overdueMinutes);
  }

  if (days >= 2) return `Due ${target.toFormat("ccc h:mm a")}`;
  if (days === 1) return `Due tomorrow ${target.toFormat("h:mm a")}`;
  if (hours >= 1) return `Due in ${hours}h`;
  if (minutes >= 1) return `Due in ${minutes}m`;
  return "Due now";
};

/**
 * The dot colour a due follow-up shows in tab labels and panel headers. Lives
 * beside the urgency it reads so the two stay in step - the mapping was drifting
 * as a copy per call site.
 */
export const getFollowUpDotClassName = (
  dueAt: string | null | undefined,
): string => {
  const urgency = getFollowUpUrgency(dueAt);

  if (urgency === FOLLOW_UP_URGENCY.EXPIRED) return "bg-danger";
  if (urgency === FOLLOW_UP_URGENCY.APPROACHING) return "bg-warning";

  return "bg-muted";
};
